import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, Download } from 'lucide-react';
import { useBusinessPaymentsById } from '../hooks/useBusinessPayments';
import { useBookings } from '../hooks/useBookings';
import { bookingHref, bookingsByPaymentReference, passShortRef } from './paymentBookingLink';
import { PaymentActionsHost } from './PaymentActionsHost';
import { formatPaymentDate, formatCurrency, formatCardNumber, lastFour, gatewayLabel, netPaidAmount, paymentChipOf } from '../utils/paymentUtils';
import { TX_CHIP_LABEL } from '../pages/BusinessPage/activityViews';
import type { Payment } from '../types/payment.types';

/** Status → reuse the bz-access-status pill colors (confirmed=green, cancelled=red). */
const statusClass = (p: Payment): string => {
  const chip = paymentChipOf(p);
  if (chip === 'paid' || chip === 'partiallyRefunded') return 'confirmed';
  if (chip === 'failed' || chip === 'voided') return 'cancelled';
  return '';
};
const statusText = (p: Payment): string => {
  const chip = paymentChipOf(p);
  return chip ? TX_CHIP_LABEL[chip] : p.payment_status || '—';
};

type StatusFilter = 'all' | 'success' | 'failed' | 'voided';

const STATUS_OPTIONS: Array<{ value: StatusFilter; label: string }> = [
  { value: 'all', label: 'All statuses' },
  { value: 'success', label: 'Success' },
  { value: 'failed', label: 'Failed' },
  { value: 'voided', label: 'Voided' },
];

/**
 * Payments table for a single business, scoped to the business detail panel.
 * Calls the same `/payments/{businessId}` endpoint the /payments page uses,
 * with a search box (reference / txn ref) and status filter like the prototype.
 */
export const BusinessPaymentsTable: React.FC<{ businessId: string }> = ({ businessId }) => {
  const { data, isLoading } = useBusinessPaymentsById(businessId);
  const payments = data?.payments ?? [];
  // SAM-729 — the booking each payment paid for, joined on the gateway reference.
  const { data: bookings } = useBookings(businessId);
  const bookingByRef = useMemo(() => bookingsByPaymentReference(bookings), [bookings]);
  const bookingFor = (p: Payment) => (p.payment_reference ? bookingByRef.get(p.payment_reference.trim()) : undefined);

  const [query, setQuery] = useState('');
  const [status, setStatus] = useState<StatusFilter>('all');
  // A row opens the payment's detail drawer, with Void and Refund as in the Payments page.
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);

  const filtered = useMemo(() => {
    let list = payments;
    if (status === 'success') list = list.filter((p) => { const c = paymentChipOf(p); return c === 'paid' || c === 'partiallyRefunded'; });
    else if (status !== 'all') list = list.filter((p) => p.payment_status === status);
    const q = query.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (p) =>
          p.payment_reference?.toLowerCase().includes(q) ||
          p.transaction_reference?.toLowerCase().includes(q) ||
          lastFour(p.cardLast4).toLowerCase().includes(q) ||
          String(p.business_access_id ?? '').toLowerCase().includes(q) ||
          (p.payment_reference ? bookingByRef.get(p.payment_reference.trim())?.code.toLowerCase().includes(q) : false),
      );
    }
    return list;
  }, [payments, query, status, bookingByRef]);

  // Summary metrics over the currently-filtered set (matches prototype behavior).
  const summary = useMemo(() => {
    const count = filtered.length;
    const net = filtered.reduce((s, p) => s + netPaidAmount(p), 0);
    const voided = filtered
      .filter((p) => p.payment_status === 'voided')
      .reduce((s, p) => s + (p.payment_amount || 0), 0);
    const failed = filtered.filter((p) => p.payment_status === 'failed').length;
    return { count, net, voided, failed };
  }, [filtered]);

  const exportCsv = () => {
    const header = ['Booking / Pass', 'Reference', 'Txn Ref', 'Date', 'Amount', 'Card', 'Gateway', 'Status'];
    const rows = filtered.map((p) => [
      bookingFor(p)?.code || (p.business_access_id ? `Pass ${passShortRef(p.business_access_id)}` : ''),
      p.payment_reference || '',
      p.transaction_reference || '',
      p.payment_date || '',
      p.payment_amount ?? '',
      lastFour(p.cardLast4),
      gatewayLabel(p.payment_gateway) === '—' ? '' : gatewayLabel(p.payment_gateway),
      p.payment_status || '',
    ]);
    const csv = [header, ...rows]
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `payments-${businessId}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (isLoading) return <div className="bz-list-empty">Loading payments…</div>;
  if (!payments.length) return <div className="bz-list-empty">No payments yet for this business.</div>;

  return (
    <div>
      <div className="bz-pay-cards">
        <div className="bz-pay-card">
          <div className="bz-pay-card-label">Transactions</div>
          <div className="bz-pay-card-value">{summary.count.toLocaleString()}</div>
        </div>
        <div className="bz-pay-card">
          <div className="bz-pay-card-label">Net revenue</div>
          <div className="bz-pay-card-value">{formatCurrency(summary.net)}</div>
        </div>
        <div className="bz-pay-card">
          <div className="bz-pay-card-label">Voided</div>
          <div className="bz-pay-card-value">{formatCurrency(summary.voided)}</div>
        </div>
        <div className="bz-pay-card">
          <div className="bz-pay-card-label">Failed</div>
          <div className="bz-pay-card-value">{summary.failed.toLocaleString()}</div>
        </div>
      </div>

      <div className="bz-pay-filters">
        <div className="bz-search bz-pay-search">
          <span className="bz-search-icon"><Search size={14} /></span>
          <input
            className="bz-search-input"
            placeholder="Search booking, pass, reference or card…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <select
          className="bz-pay-select"
          value={status}
          onChange={(e) => setStatus(e.target.value as StatusFilter)}
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <button
          type="button"
          className="bz-secondary-btn"
          style={{ marginLeft: 'auto' }}
          onClick={exportCsv}
          disabled={!filtered.length}
        >
          <Download size={14} />
          Export CSV
        </button>
      </div>

      {filtered.length === 0 ? (
        <div className="bz-list-empty">No payments match your filters.</div>
      ) : (
        <div className="biz-wide" style={{ padding: 0 }}>
          <table className="bz-access-table bz-wide-table">
            <thead>
              <tr>
                <th>Booking / Pass</th>
                <th>Reference</th>
                <th>Date</th>
                <th>Amount</th>
                <th>Card</th>
                <th>Gateway</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p, i) => {
                const booking = bookingFor(p);
                return (
                <tr
                  key={p.transaction_reference || p.payment_reference || i}
                  tabIndex={0}
                  aria-label={`Open payment ${p.payment_reference || p.transaction_reference || ''}`.trim()}
                  style={{ cursor: 'pointer' }}
                  onClick={(e) => { if (!(e.target as HTMLElement).closest('a,button')) setSelectedPayment(p); }}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !(e.target as HTMLElement).closest('a,button')) setSelectedPayment(p); }}
                >
                  <td style={{ fontFamily: 'var(--font-monospace, monospace)', fontSize: 12, whiteSpace: 'nowrap' }}>
                    {booking ? (
                      <Link to={bookingHref(booking.id, businessId)} title="Open booking">
                        {booking.code || 'Open booking'}
                      </Link>
                    ) : p.business_access_id ? (
                      // A payment of a pass (no booking): the row opens the payment; its detail has «View pass».
                      <span>Pass {passShortRef(p.business_access_id)}</span>
                    ) : '—'}
                  </td>
                  <td style={{ fontFamily: 'var(--font-monospace, monospace)', fontSize: 12 }}>
                    {p.payment_reference || '—'}
                  </td>
                  <td>{formatPaymentDate(p.payment_date)}</td>
                  <td>{formatCurrency(p.payment_amount)}</td>
                  <td>{formatCardNumber(p.cardLast4)}</td>
                  <td>{gatewayLabel(p.payment_gateway)}</td>
                  <td>
                    <span className={`bz-access-status ${statusClass(p)}`}>
                      {statusText(p)}
                    </span>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <PaymentActionsHost payment={selectedPayment} onClose={() => setSelectedPayment(null)} selectedBusinessId={businessId} />
    </div>
  );
};
